import React from 'react'
import styled from 'styled-components'
import BuyButton from '../../MwPage/components/BuyButton'
import RichText from '../../MwPage/components/RichText'
import { cyan, onDark2, PHONE, sans, teal, ui } from '../../MwPage/styled'
import { fillTrial } from '../content/trial'
import { useFocus } from '../focus'
import { Note } from '../styled'

// the prototype's .btn on a trial page: the label over a smaller "7 days free, then …" line
const TwoLine = styled(BuyButton)`
  gap: 16px;
  min-height: 64px;
  padding: 0 28px;
  text-align: left;

  span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  b {
    font-size: 18px;
    line-height: 1.15;
  }

  small {
    font: 500 13px/1.3 ${sans};
    opacity: 0.9;
  }

  @media ${PHONE} {
    justify-content: space-between;
  }
`

// the prototype's .trial line under a button
const TrialNote = styled(Note)`
  font-size: 14px;

  strong {
    font-family: ${ui};
    color: ${(props) => (props.$onDark ? cyan : teal)};
  }
`

// the button with its lines under it; the lines centre under a full-width button, which every button is on a phone
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  ${(props) => props.$center && 'align-items: center; text-align: center;'}

  @media ${PHONE} {
    align-items: center;
    text-align: center;
  }
`

const Limited = styled.span`
  display: inline-block;
  font: 700 12px/1.3 ${ui};
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${(props) => (props.$onDark ? onDark2 : teal)};
`

/**
 * The free-trial buy button: "Start your free trial" with the price line inside it. The sub line waits for the
 * visitor's prices.
 */
export const TrialButton = ({ offer, children, ...rest }) => {
  const { trial } = useFocus()
  const sub = fillTrial(trial.ctaSub, offer)
  return (
    <TwoLine {...rest}>
      <span>
        <b>{children || trial.cta}</b>
        {sub && <small>{sub}</small>}
      </span>
    </TwoLine>
  )
}

/**
 * The button with the trial's terms under it, as the hero, the "plan starts here" card and the closing panel show it.
 *
 * @param line     which line goes under the button: the billing `note` (default), the short `fine` print, or the
 *                 `closing` panel's cancel line
 * @param limited  true to add the "Limited Time Offer · Capacity Driven" line
 * @param centre   centre the button and its lines without stretching the button, on a centred section
 */
const TrialCta = ({ offer, onDark = false, block = false, line = 'note', limited = false, centre = false, style }) => {
  const { trial } = useFocus()
  const note = fillTrial(trial[line], offer)
  return (
    <Stack $center={block || centre} style={style}>
      <TrialButton offer={offer} $block={block} style={block || centre ? undefined : { alignSelf: 'flex-start' }} />
      {note && (
        <TrialNote $onDark={onDark}>
          <RichText text={note} />
        </TrialNote>
      )}
      {limited && <Limited $onDark={onDark}>{trial.limited}</Limited>}
    </Stack>
  )
}

export default TrialCta
